import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { api, type EmailOrganization, type EmailOrgPayload } from '../services/api'

const EMPTY_FORM: EmailOrgPayload = {
  name: '',
  allowed_emails: [],
  allowed_domains: [],
  is_active: true,
}

function parseLines(text: string): string[] {
  return text.split('\n').map((l) => l.trim()).filter(Boolean)
}

function toLines(arr: string[]): string {
  return arr.join('\n')
}

interface OrgFormProps {
  initial: EmailOrgPayload
  onSave: (payload: EmailOrgPayload) => void
  onCancel: () => void
  saving: boolean
  error: string
}

function OrgForm({ initial, onSave, onCancel, saving, error }: OrgFormProps) {
  const [name, setName] = useState(initial.name)
  const [emailText, setEmailText] = useState(toLines(initial.allowed_emails))
  const [domainText, setDomainText] = useState(toLines(initial.allowed_domains))
  const [isActive, setIsActive] = useState(initial.is_active)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSave({
      name: name.trim(),
      allowed_emails: parseLines(emailText),
      allowed_domains: parseLines(domainText),
      is_active: isActive,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="form-control">
        <label className="label"><span className="label-text font-medium">Organization name</span></label>
        <input
          type="text"
          className="input input-bordered w-full"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="Acme Corporation"
        />
      </div>

      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Allowed email addresses</span>
          <span className="label-text-alt text-base-content/50">one per line</span>
        </label>
        <textarea
          className="textarea textarea-bordered font-mono text-sm h-24"
          value={emailText}
          onChange={(e) => setEmailText(e.target.value)}
          placeholder="alice@acme.com"
        />
      </div>

      <div className="form-control">
        <label className="label">
          <span className="label-text font-medium">Allowed email domains</span>
          <span className="label-text-alt text-base-content/50">e.g. acme.com, one per line</span>
        </label>
        <textarea
          className="textarea textarea-bordered font-mono text-sm h-20"
          value={domainText}
          onChange={(e) => setDomainText(e.target.value)}
          placeholder="acme.com"
        />
      </div>

      <div className="form-control">
        <label className="label cursor-pointer justify-start gap-3">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          <span className="label-text">Active</span>
        </label>
      </div>

      {error && <p className="text-error text-sm">{error}</p>}

      <div className="flex gap-2 justify-end pt-2">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !name.trim()}>
          {saving ? <span className="loading loading-spinner loading-xs" /> : 'Save'}
        </button>
      </div>
    </form>
  )
}

export default function EmailOrganizations() {
  const queryClient = useQueryClient()
  const [searchParams] = useSearchParams()
  const selectedOrganizationId = searchParams.get('organization')
  const [modalOrg, setModalOrg] = useState<EmailOrganization | null | 'new'>(null)
  const [deleteTarget, setDeleteTarget] = useState<EmailOrganization | null>(null)
  const [formError, setFormError] = useState('')

  const { data: orgs, isLoading, error } = useQuery({
    queryKey: ['email-organizations'],
    queryFn: api.getEmailOrganizations,
    staleTime: 30000,
  })

  const createMutation = useMutation({
    mutationFn: (payload: EmailOrgPayload) => api.createEmailOrganization(payload),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['email-organizations'] }); setModalOrg(null); setFormError('') },
    onError: (e: Error) => setFormError(e.message),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<EmailOrgPayload> }) =>
      api.updateEmailOrganization(id, payload),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['email-organizations'] }); setModalOrg(null); setFormError('') },
    onError: (e: Error) => setFormError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteEmailOrganization(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['email-organizations'] }); setDeleteTarget(null) },
  })

  const handleSave = (payload: EmailOrgPayload) => {
    setFormError('')
    if (modalOrg === 'new') {
      createMutation.mutate(payload)
    } else if (modalOrg) {
      updateMutation.mutate({ id: modalOrg.id, payload })
    }
  }

  const saving = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    if (!selectedOrganizationId || !orgs?.some((org) => org.id === selectedOrganizationId)) return
    document.getElementById(`email-organization-${selectedOrganizationId}`)?.scrollIntoView({ block: 'center' })
  }, [orgs, selectedOrganizationId])

  if (isLoading) return <div className="p-8"><span className="loading loading-spinner loading-lg" /></div>
  if (error) return <div className="p-8 text-error">Failed to load email organizations.</div>

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="page-title">Email Organizations</h1>
        <button className="btn btn-primary btn-sm" onClick={() => { setModalOrg('new'); setFormError('') }}>
          + New Organization
        </button>
      </div>

      {(!orgs || orgs.length === 0) ? (
        <div className="text-center py-16 text-base-content/50">No email organizations yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Emails</th>
                <th>Domains</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr
                  key={org.id}
                  id={`email-organization-${org.id}`}
                  className={org.id === selectedOrganizationId ? 'bg-primary/10 outline outline-1 outline-primary/40' : ''}
                >
                  <td className="font-medium">{org.name}</td>
                  <td className="text-sm font-mono opacity-70">{org.allowed_emails.length > 0 ? org.allowed_emails.join(', ') : '—'}</td>
                  <td className="text-sm font-mono opacity-70">{org.allowed_domains.length > 0 ? org.allowed_domains.join(', ') : '—'}</td>
                  <td>
                    <span className={`badge badge-sm ${org.is_active ? 'badge-success' : 'badge-ghost'}`}>
                      {org.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex gap-2 justify-end">
                      <button className="btn btn-ghost btn-xs" onClick={() => { setModalOrg(org); setFormError('') }}>Edit</button>
                      <button className="btn btn-error btn-xs btn-outline" onClick={() => setDeleteTarget(org)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOrg !== null && (
        <dialog open className="modal modal-open">
          <div className="modal-box max-w-lg">
            <h3 className="font-bold text-lg mb-4">{modalOrg === 'new' ? 'New Email Organization' : `Edit — ${(modalOrg as EmailOrganization).name}`}</h3>
            <OrgForm
              initial={modalOrg === 'new' ? EMPTY_FORM : {
                name: (modalOrg as EmailOrganization).name,
                allowed_emails: (modalOrg as EmailOrganization).allowed_emails,
                allowed_domains: (modalOrg as EmailOrganization).allowed_domains,
                is_active: (modalOrg as EmailOrganization).is_active,
              }}
              onSave={handleSave}
              onCancel={() => { setModalOrg(null); setFormError('') }}
              saving={saving}
              error={formError}
            />
          </div>
          <form method="dialog" className="modal-backdrop"><button onClick={() => setModalOrg(null)}>close</button></form>
        </dialog>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-2">Delete organization?</h3>
            <p className="text-base-content/70 mb-6">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button className="btn btn-ghost btn-sm" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button
                className="btn btn-error btn-sm"
                onClick={() => deleteMutation.mutate(deleteTarget.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? <span className="loading loading-spinner loading-xs" /> : 'Delete'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop"><button onClick={() => setDeleteTarget(null)}>close</button></form>
        </dialog>
      )}
    </div>
  )
}
